import moment from 'moment';
import React, {
  useEffect,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import { Popover } from 'antd';

// import LoadingIcon from '../../components/shared/LoadingIcon';
import Link from '../../components/shared/Link';
import SingleLineChart from '../../components/shared/SingleLineChart';
import i18n from '../../config/i18n';
import {
  FPP_ASSESSMENT_DETAIL,
  FPP_ASSESSMENT_HISTORY,
} from '../../constants/exportPDFTypes';
import { FPP_SECTION } from '../../constants/SectionNames';

const styles = {
  wrapper: {
    marginBottom: '5%',
  },
  table: {
    marginBottom: 0,
  },
  thead: {
    backgroundColor: '#607484',
    color: '#FFF',
  },
  th: {
    fontSize: 23,
  },
  edit: {
    float: 'right',
  },
  graph: {
    width: '100%',
    height: '100%',
  },
};


const FPPContainer = ({
  actions: {
    checkExportPDFPermission,
    getFPPRecords,
  },
  assessments,
  // isLoading, // we can use property once we apply table from antd
  medicalRecordId,
  onExportPDF,
  router,
  targetSection,
  viewOnly,
}) => {
  const fppSectionRef = useRef();

  const init = async () => {
    await getFPPRecords(medicalRecordId);
    if (targetSection === FPP_SECTION && fppSectionRef && fppSectionRef.current) {
      window.scrollTo(0, fppSectionRef.current.offsetTop + 140);
    }
  };

  useEffect(() => {
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // const prepareLogLoadingHtml = () => (
  //   <tr>
  //     <td><LoadingIcon /></td>
  //     <td />
  //     <td />
  //     <td />
  //   </tr>
  // );

  const handleRowExportClick = async (e) => {
    e.preventDefault();
    const assessmentId = parseInt(e.currentTarget.name, 10);
    if (await checkExportPDFPermission()) {
      onExportPDF(assessmentId, FPP_ASSESSMENT_DETAIL);
    }
  };

  const prepareLogsListHtml = (item, index, array) => {
    const assessmentId = item.id;
    return (
      <tr key={assessmentId}>
        <td>{array.length - index}</td>
        <td>{moment(item.createdAt).format('YYYY-MM-DD hh:mm:ss A')}</td>
        <td>
          {item.totalScore}
        </td>
        <td style={{ width: 150, justifyContent: 'right' }}>

          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            {viewOnly
              ? null
              : (
                <div>
                  <Popover
                    content={i18n.t('exportFPPDetailsPopoverBody')}
                  >
                    <button
                      className=" btn-info btn-sm"
                      onClick={handleRowExportClick}
                      name={assessmentId}
                    >
                      {i18n.t('export')}
                    </button>
                  </Popover>
                </div>
              )}
            <div>
              <Link
                href={`/medical-records/${medicalRecordId}/fpp/1/assessment/${assessmentId}`}
                router={router}
              >
                <button className=" btn-info btn-sm">
                  {i18n.t('view')}
                </button>
              </Link>
            </div>
          </div>
        </td>
      </tr>
    );
  };

  const prepareLogsHtml = () => (
    assessments.slice().reverse().map(prepareLogsListHtml)
  );

  // basically we has only one kind of FPP survey which means there is one line only
  // so we directly map history into the first data set
  const parseChartData = (records = []) => (
    records.map(({
      createdAt,
      totalScore,
    }) => ({
      date: moment(createdAt).format('YY/MM/DD'),
      score: totalScore,
    }))
  );

  const handleClickExportAssessmentHistory = async (e) => {
    e.preventDefault();
    if (await checkExportPDFPermission()) {
      onExportPDF(parseInt(medicalRecordId, 10), FPP_ASSESSMENT_HISTORY);
    }
  };

  const {
    wrapper,
    table,
    thead,
    th,
    edit,
    graph,
  } = styles;

  return (
    <div className="row" ref={fppSectionRef}>
      <div className="col-md-12">
        <div style={wrapper}>
          <table className="table table-responsive table-bordered" style={table}>
            <thead style={thead}>
              <tr>
                <th style={th}>
                  {i18n.t('fppProgram')}
                  <span style={edit}>
                    {viewOnly
                      ? null
                      : (
                        <div className="btn-group">
                          <Popover
                            content={i18n.t('exportFPPHistoryPopoverBody')}
                          >
                            <button
                              className="btn btn-warning btn-sm"
                              onClick={handleClickExportAssessmentHistory}
                            >
                              {i18n.t('exportFPPHistory')}
                            </button>
                          </Popover>
                          <Link
                            href={`/medical-records/${medicalRecordId}/fpp/1/add`}
                            router={router}
                          >
                            <button className="btn btn-success btn-sm" >
                              {i18n.t('addAssessment')}
                            </button>
                          </Link>
                        </div>
                      )
                    }
                  </span>
                </th>
              </tr>
            </thead>
          </table>
          <div style={graph}>
            {assessments.length > 0 && (
              <SingleLineChart
                height={300}
                data={parseChartData(assessments)}
                noTitle
              />
            )}
          </div>
          <table className="table table-striped table-hover table-responsive">
            <thead style={thead}>
              <tr>
                <th />
                <th>{i18n.t('assessmentTime')}</th>
                <th>{i18n.t('totalScore')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {prepareLogsHtml()}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

FPPContainer.propTypes = {
  actions: PropTypes.shape({
    checkExportPDFPermission: PropTypes.func.isRequired,
    getFPPRecords: PropTypes.func.isRequired,
  }).isRequired,
  assessments: PropTypes.arrayOf(PropTypes.shape({
    createdAt: PropTypes.string,
    totalScore: PropTypes.number,
  })),
  isLoading: PropTypes.bool,
  medicalRecordId: PropTypes.string.isRequired,
  onExportPDF: PropTypes.func.isRequired,
  router: PropTypes.object.isRequired,
  targetSection: PropTypes.string,
  viewOnly: PropTypes.bool,
};

FPPContainer.defaultProps = {
  assessments: [],
  isLoading: false,
  targetSection: '',
  viewOnly: true,
};

export default FPPContainer;



// WEBPACK FOOTER //
// ./src/containers/fpp/FPPContainer.js